import { SupportedLocale } from "@/i18n";
import { LoginResponse } from "@/lib/auth/auth-types";
import { RegionCode } from "@/lib/axiosConfig";
import { createContext, useContext } from "react";
import { visibleT } from "@/lib/cms/cms-new-i18n";

interface CMS {
  id: string;
  instanceId: string;
  /** Mode d'accès BO : SIMPLE | ADVANCED | MIXED. */
  cmsAccessMode?: "SIMPLE" | "ADVANCED" | "MIXED";
  /** CMS avancé activé pour l'instance — pilote BO + rendu public. */
  advancedCmsEnabled?: boolean;
}

export interface Instance {
  id: string;
  name: string;
  frontUrl: string;
  s3Bucket: string;
  CMS: CMS;
  hideArtMatchFront: boolean;
  hideArtMatchBO: boolean;
  isModulePhotoEnabled: boolean;
  isModuleSculptureEnabled: boolean;
  oeuvresEnVenteURL: string;
  haveEshop: boolean;
  hideCr?: boolean;
  watermarkEnabled?: boolean;
  hideNewArtworkAutheticationForm?: boolean;
  /** When true, the user can change the back office UI language from the account page. */
  authorizeBoLangChange?: boolean;
  /** When true, artwork/sculpture history tabs are re-labeled "Provenance" and merge public auctions. */
  provenanceMode?: boolean;
  itinerantExhibitionGrouped?: boolean;
  /**
   * Per-instance default (set true for instances created after this feature):
   * maskable BO fields start hidden until each user shows them. Only a default —
   * per-user preferences win and stay editable. False/undefined keeps the legacy
   * all-visible behaviour for existing instances.
   */
  defaultFieldsMasked?: boolean;
  /**
   * Per-instance default (set true for new instances): the artwork sheet opens in
   * "essentiel" (simplified) mode until the user switches to "pro". Only a default.
   */
  defaultArtworkViewEssentiel?: boolean;
}

/** Tokens indexés par code de région. */
export type RegionTokens = Record<RegionCode, string>;

export interface MultiRegionLoginResult {
  /** Première réponse réussie (utilisée pour la nav, ex: 2FA → /2fa). */
  primaryResponse: LoginResponse;
  tokens: RegionTokens;
}

interface AuthContextType {
  /** Token de la région active (compatibilité descendante). */
  token: string | null;
  /** Tokens disponibles après login parallèle, indexés par code de région. */
  regionTokens: RegionTokens;
  login: (
    username: string,
    password: string,
    locale: SupportedLocale,
  ) => Promise<MultiRegionLoginResult>;
  twoFactorLogin: (
    email: string,
    twoFactorToken: string,
    locale: SupportedLocale,
  ) => Promise<LoginResponse>;
  logout: () => void;
  instance: string | undefined;
  instanceData: Instance | undefined;
  checkToken: () => boolean;
  checkInstanceAndToken: () => boolean;
  /**
   * True once BO preferences have been hydrated from the server for the
   * active instance (or when no session/instance is selected yet).
   * Dashboard routes must wait for this before mounting tables, otherwise
   * mount-time persists overwrite server prefs with defaults.
   */
  boPreferencesReady: boolean;
  /**
   * Sélectionne une instance et mémorise la région associée pour que
   * l'intercepteur Axios route les requêtes vers la bonne API.
   */
  setInstance: (instanceName: string, regionCode?: RegionCode) => void;
  user: {
    id: string;
    email: string;
    authToken: string;
    role: string;
  } | null;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(
      visibleT("bo.auth.useAuthProviderError", "useAuth doit être utilisé dans un AuthProvider"),
    );
  }
  return context;
};
